import moment from 'jalali-moment';
import { TFunction } from 'i18next';
import { envConfig } from '@/configs';

/** Detail keys removed from activity log UI when `removePrices` is enabled */
const ACTIVITY_PRICE_DETAIL_KEYS = [
  'price', 'subsidyAmount', 'actualSubsidyAmount', 'remainCredit',
  'amount', 'diff', 'sentCredit'
] as const;

/** Shallow clone of activity `details` without price-like fields when `removePrices` is on (e.g. modal payload at call site). */
export function activityLogDetailsWithoutPriceFields(
  details: ActivityReportItemEntity['details'] | undefined
): ActivityReportItemEntity['details'] | undefined {
  if (!details || !envConfig.ui.removePrices) {
    return details;
  }
  const next = { ...details };
  for (const k of ACTIVITY_PRICE_DETAIL_KEYS) {
    delete next[k];
  }
  return next;
}

const ACTIVITY_TYPE_ONLINE_DELIVERY = [
  'OnlineFoodDeliveryByCardType',
  'OnlineFoodDeliveryByCvv2Type',
  'OnlineFoodDeliveryByFaceMatchingType',
  'OnlineFoodDeliveryByForgottenCodeType',
  'OnlineFoodDeliveryByCvv2WithFaceMatchingType',
  'OnlineFoodDeliveryByCardWithFaceMatchingType',
  'OnlineFoodDeliveryByForgottenCodeWithFaceMatchingType',
];

export function mapItems(items: ActivityReportEntity[], findVariantById = undefined, t: TFunction): ActivityReportItemEntity[] {

  const result: ActivityReportItemEntity[] = [];
  const txtUnknown = t('common:labels.unknown');
  const txtYes = t('common:labels.yes');
  const txtNo = t('common:labels.no');

  function detailItem(title: string, value: any) {
    return ({
      title,
      value: value || txtUnknown
    });
  }

  function fieldValueText(key: string): any {
    return (t(`report:activity-report-activity-fields.${key}`, { returnObjects: true, defaultValue: '' }));
  }

  function fieldTypeText(key: string) {
    return (t(`report:activity-report-action-types.${key}`));
  }

  function formatTimestamp(value: string) {
    if (value == null || value === '') {
      return '';
    }
    return moment(new Date(Number(value))).format('jYYYY/jMM/jDD');
  }

  for (const item of items) {
    const fullClassname = item.activityTypeClassName;
    const classname = fullClassname.substring(
      fullClassname.lastIndexOf('.') + 1,
      fullClassname.length
    );

    const data = JSON.parse(item.activityTypeJson);

    const occurDate = moment(new Date(item.occurDate)).format('jYYYY/jMM/jDD');
    const occurTime = moment(new Date(item.occurDate)).format('HH:mm');
    const serveDate = data.serveDate ? moment(new Date(data.serveDate)).format('jYYYY/jMM/jDD') : '';

    const newItem: ActivityReportItemEntity = {
      name: fieldTypeText(classname) || txtUnknown,
      price: envConfig.ui.removePrices
        ? ''
        : (data['price'] || data['diff'] || data['sentCredit'] || data['receivedCredit'] || data['amount'] || '-'),
      date: occurDate,
      time: occurTime,
      fullDate: occurDate + ' ' + occurTime,
      serveDate: serveDate,
      creditType: data.creditType
        ? t(`report:credit-type-values.${data.creditType}`, { defaultValue: data.creditType })
        : '',
      details: {
        firstName: detailItem(fieldValueText('firstName'), item.user && item.user.firstName),
        lastName: detailItem(fieldValueText('lastName'), item.user && item.user.lastName),
        groupName: detailItem(fieldValueText('groupName'), item.groupName),
        username: detailItem(fieldValueText('username'), item.user && item.user.username),
      }
    };

    const propertyArray = Object.getOwnPropertyNames(data).filter((item) => (
      !['type', 'actionType', 'issueDate', 'progId', 'selfCode'].includes(item)
    ));

    let variant = findVariantById ? findVariantById(Number(data.selfCode)) : 'nurture';

    if (variant === 'unknown') {
      variant = 'nurture';
    }

    for (let i = 0; i < propertyArray.length; i++) {
      if (['dailySale', 'consumed'].includes(propertyArray[i])) {
        // DailySale is a boolean type which should be treated as positive negative value
        newItem.details[propertyArray[i]] = {
          title: fieldValueText(propertyArray[i]) as any,
          value: data[propertyArray[i]] ? txtYes : txtNo,
        };
      }
      else if (['price', 'subsidyAmount', 'actualSubsidyAmount', 'remainCredit'].includes(propertyArray[i])) {
        if (!envConfig.ui.removePrices) {
          newItem.details[propertyArray[i]] = {
            title: fieldValueText(propertyArray[i]) as any,
            value: envConfig.ui.removeCurrency ?
              Math.ceil(data[propertyArray[i]] / 10) : // convert to toman if needed
              data[propertyArray[i]],
          };
        }
      }
      else if (propertyArray[i] === 'creditType') {
        newItem.details[propertyArray[i]] = {
          title: fieldValueText('creditType') as any,
          value: t(`report:credit-type-values.${data[propertyArray[i]]}`, { defaultValue: data[propertyArray[i]] }),
        };
      }
      else if (propertyArray[i] === 'expirationDate') {
        newItem.details[propertyArray[i]] = {
          title: fieldValueText('expirationDate') as any,
          value: formatTimestamp(data[propertyArray[i]]) || txtUnknown,
        };
      }
      else {
        const val = fieldValueText(propertyArray[i]);
        if (typeof val === 'object') {
          newItem.details[propertyArray[i]] = {
            title: val?.[variant],
            value: data[propertyArray[i]],
          };
        }
        else {
          newItem.details[propertyArray[i]] = {
            title: val,
            value: data[propertyArray[i]],
          };
        }
      }
    }

    if (ACTIVITY_TYPE_ONLINE_DELIVERY.includes(classname)) {

      newItem.details['teammateUsername'] = {
        title: fieldValueText('teammateUsername'),
        value: (item.operator && item.operator?.username ? item.operator?.username : '-') || '-',
      };

      newItem.details['teammateFullname'] = {
        title: fieldValueText('teammateFullname'),
        value: (item.operator && item.operator?.firstName ? item.operator?.lastName : '-') || '-',
      };
    }
    else {
      newItem.details['operatorFirstName'] = {
        title: fieldValueText('operatorFirstName'),
        value: (item.operator && item.operator.firstName) || '-',
      };
      newItem.details['operatorLastName'] = {
        title: fieldValueText('operatorLastName'),
        value: (item.operator && item.operator.lastName) || '-',
      };
      newItem.details['operatorUsername'] = {
        title: fieldValueText('operatorUsername'),
        value: (item.operator && item.operator.lastName) || '-',
      };
    }

    if (newItem.details.serveDate) {
      newItem.details.serveDate.value = serveDate;
    }

    if (envConfig.ui.removePrices) {
      for (const k of ACTIVITY_PRICE_DETAIL_KEYS) {
        delete newItem.details[k];
      }
    }

    result.push(newItem);
  }


  return result;
}
